import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import MultiMapView, { MapProject } from '../../components/common/MultiMapView';
import { DASHBOARD_STATS_QUERY } from '../../graphql/queries';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Dashboard.module.css';
import Icon from '../../components/common/Icon';

const formatStorage = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  if (diffH < 48) return 'Yesterday';
  return date.toLocaleDateString();
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(DASHBOARD_STATS_QUERY);

  const stats = data?.dashboardStats;
  const statText = (value: () => string) => (loading ? '...' : error ? '—' : value());

  const mapProjects: MapProject[] = (stats?.recentProjects ?? [])
    .filter((p: { latitude?: number; longitude?: number }) => p.latitude != null && p.longitude != null)
    .map((p: { id: string; name: string; location?: string; latitude: number; longitude: number }) => ({
      id: p.id,
      name: p.name,
      location: p.location,
      latitude: p.latitude,
      longitude: p.longitude,
    }));

  // Real storage capacity from the user's plan; null when they have no limit.
  const storagePct = user?.accountStorageLimit
    ? Math.min(100, ((user.accountStorageUsed ?? 0) / user.accountStorageLimit) * 100)
    : null;

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className={`flex justify-between items-end ${styles.pageHeader}`}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Engineering Dashboard</h2>
            <p className="text-on-surface-variant mt-1">Real-time geospatial project overview and asset management.</p>
          </div>
          <button
            onClick={() => navigate('/projects', { state: { openCreate: true } })}
            className="btn-primary bg-primary hover:bg-primary-container px-6 py-2.5 rounded-lg text-sm shadow-lg"
          >
            <Icon name="add" className="text-sm" />
            New Project
          </button>
        </div>

        {error && (
          <div role="alert" className="p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <Icon name="error" className="text-base" />
            Could not load your dashboard: {error.message}
          </div>
        )}

        <div className={styles.statsMapRow}>
          <div className={styles.statsGrid}>
            {([
              {
                icon: 'folder_managed',
                label: 'Total Projects',
                value: statText(() => (stats?.totalProjects ?? 0).toString()),
                badge: null as string | null,
                badgeClass: '',
                iconBg: 'bg-primary/10',
                iconColor: 'text-primary',
              },
              {
                icon: 'article',
                label: 'Total Files',
                value: statText(() => (stats?.totalFiles ?? 0).toLocaleString()),
                badge: null as string | null,
                badgeClass: '',
                iconBg: 'bg-secondary-container/30',
                iconColor: 'text-secondary',
              },
              {
                icon: 'hard_drive',
                label: 'Storage Used',
                value: statText(() => formatStorage(stats?.storageUsed ?? 0)),
                // Real usage against the plan limit — hidden when the user has no limit.
                badge: storagePct != null ? `${storagePct.toFixed(0)}% Capacity` : null,
                badgeClass: 'text-[10px] font-bold text-on-surface-variant',
                iconBg: 'bg-tertiary/10',
                iconColor: 'text-tertiary',
              },
              {
                icon: 'groups',
                label: 'Collaborators',
                value: statText(() => (stats?.totalMembers ?? 0).toString()),
                badge: null as string | null,
                badgeClass: '',
                iconBg: 'bg-primary-container/10',
                iconColor: 'text-primary-container',
              },
            ] as const).map((s) => (
              <div key={s.label} className={styles.statCard}>
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
                    <Icon name={s.icon} />
                  </div>
                  {s.badge && <span className={s.badgeClass}>{s.badge}</span>}
                </div>
                <p className="text-on-surface-variant text-xs font-medium uppercase tracking-wider">{s.label}</p>
                <h3 className="text-2xl font-bold mt-1 font-headline">{s.value}</h3>
              </div>
            ))}
          </div>

          <div className={styles.mapPanel}>
            <MultiMapView projects={mapProjects} />
          </div>
        </div>

        <div className={styles.bentoGrid}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-on-surface font-headline">Recent Projects</h3>
              <button
                onClick={() => navigate('/projects')}
                className="text-primary text-sm font-semibold flex items-center gap-1 hover:underline"
              >
                View All <Icon name="chevron_right" className="text-sm" />
              </button>
            </div>

            <div className={`bg-white rounded-xl ${styles.tableWrap}`}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">Project Name</th>
                    <th className="table-header">Last Edit</th>
                    <th className="table-header">Files</th>
                    <th className="table-header">Storage</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-on-surface-variant text-sm">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-on-surface-variant text-sm">Recent projects could not be loaded.</td>
                    </tr>
                  ) : stats?.recentProjects?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-on-surface-variant text-sm">
                        No projects yet. <button onClick={() => navigate('/projects', { state: { openCreate: true } })} className="text-primary hover:underline">Create one</button>
                      </td>
                    </tr>
                  ) : (
                    stats?.recentProjects?.map((project: { id: string; name: string; location?: string; updatedAt: string; fileCount: number; storageUsed: number }) => (
                      <tr key={project.id} className="table-row group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-primary/5 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all">
                              <Icon name="domain" className="text-lg" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-on-surface">{project.name}</p>
                              <p className="text-[10px] text-on-surface-variant">{project.location || 'No location'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(project.updatedAt)}</td>
                        <td className="px-6 py-4 text-xs font-medium">{project.fileCount}</td>
                        <td className="px-6 py-4 text-xs font-medium">{formatStorage(project.storageUsed)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              className="p-2 hover:bg-primary/10 rounded text-primary transition-colors"
                              title="Open"
                              onClick={() => navigate(`/projects/${project.id}`)}
                            >
                              <Icon name="open_in_new" className="text-lg" />
                            </button>
                            <button className="p-2 hover:bg-secondary/10 rounded text-secondary transition-colors" title="Share">
                              <Icon name="share" className="text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white p-6 rounded-xl space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg font-headline">Quick Actions</h3>
              </div>
              <div className="space-y-3">
                {([
                  { icon: 'add', label: 'Create New Project', action: () => navigate('/projects', { state: { openCreate: true } }) },
                  { icon: 'groups', label: 'Manage Team', action: () => navigate('/team') },
                  { icon: 'share_reviews', label: 'Sharing Center', action: () => navigate('/sharing') },
                  { icon: 'receipt_long', label: 'Activity Logs', action: () => navigate('/activity') },
                ] as const).map((item) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#131b2e] hover:text-primary hover:bg-surface-container-low transition-all"
                  >
                    <Icon name={item.icon} />
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="pt-6 border-t border-outline-variant/10 space-y-4">
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-on-surface-variant">Account Storage</span>
                    <span className="font-bold text-primary">
                      {formatStorage(user?.accountStorageUsed ?? 0)}
                      {user?.accountStorageLimit ? ` / ${formatStorage(user.accountStorageLimit)}` : ''}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-surface-container rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{
                        width: user?.accountStorageLimit
                          ? `${Math.min(100, ((user.accountStorageUsed ?? 0) / user.accountStorageLimit) * 100).toFixed(1)}%`
                          : '0%',
                      }}
                    />
                  </div>
                </div>

                {user?.planName && (
                  <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs font-medium ${
                    user.downloadsExceeded
                      ? 'bg-error-container text-on-error-container'
                      : 'bg-surface-container-low text-on-surface-variant'
                  }`}>
                    <Icon name={user.downloadsExceeded ? 'download_off' : 'download_done'} className="text-sm" />
                    {user.downloadsExceeded
                      ? 'Monthly download quota reached'
                      : 'File downloads available'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => navigate('/projects', { state: { openCreate: true } })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-primary hover:bg-primary-container text-white rounded-full flex items-center justify-center shadow-2xl transition-all hover:-translate-y-1 active:scale-95 z-50"
        title="Upload file"
      >
        <Icon name="cloud_upload" />
      </button>
    </DashboardLayout>
  );
};

export default Dashboard;
